import { useEffect, useState } from "react";
import { AppLayout, RiskBanner } from "@/components/AppLayout";
import MarketChart from "@/components/MarketChart";
import { TodayProfitCard, TourEligibilityCard, LeadershipStatusCard, TelegramSupportButton } from "@/components/DashboardCards";
import CreditLineCard from "@/components/CreditLineCard";
import TourFundCard from "@/components/TourFundCard";
import NBTokenCard from "@/components/NBTokenCard";
import NBFirstDepositModal from "@/components/NBFirstDepositModal";
import AnnouncementPopup from "@/components/AnnouncementPopup";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Bot, Crown, Users2, TrendingUp, Wallet, Copy, ArrowRight, Activity, Radar, ArrowUp, ArrowDown, Lock, Building2, FileText, Download, ArrowDownToLine, ArrowUpFromLine, Landmark } from "lucide-react";
import { Link } from "react-router-dom";
import { toast } from "sonner";

const RANK_COLORS = { core: "#22C55E", prime: "#3B82F6", elite: "#A855F7", supreme: "#EAB308" };
const RANK_LABEL = { core: "Core", prime: "Prime", elite: "Elite", supreme: "Supreme" };

// Fallback seed used only if the /market/prices endpoint fails (network error)
const TICKER_FALLBACK = [
  { pair: "BTC/USDT", price: 68432.10, change_24h: 1.24, symbol: "BTC", id: "bitcoin" },
  { pair: "ETH/USDT", price: 3865.55, change_24h: 2.11, symbol: "ETH", id: "ethereum" },
  { pair: "SOL/USDT", price: 178.32, change_24h: -0.89, symbol: "SOL", id: "solana" },
  { pair: "BNB/USDT", price: 612.45, change_24h: 0.42, symbol: "BNB", id: "binancecoin" },
  { pair: "ADA/USDT", price: 0.485, change_24h: 3.11, symbol: "ADA", id: "cardano" },
  { pair: "XRP/USDT", price: 0.614, change_24h: -1.55, symbol: "XRP", id: "ripple" },
  { pair: "AVAX/USDT", price: 42.10, change_24h: 1.88, symbol: "AVAX", id: "avalanche-2" },
  { pair: "LINK/USDT", price: 18.23, change_24h: -0.44, symbol: "LINK", id: "chainlink" },
];

// Real live prices polled from backend (CoinGecko-proxied, 30s cache on server, 20s refresh on client)
function useLivePrices() {
  const [coins, setCoins] = useState(TICKER_FALLBACK);
  const [isLive, setIsLive] = useState(false);

  useEffect(() => {
    let mounted = true;
    let iv = null;
    let failStreak = 0;
    const scheduleNext = (ms) => {
      if (!mounted) return;
      clearInterval(iv);
      iv = setInterval(fetchPrices, ms);
    };
    const fetchPrices = async () => {
      try {
        const { data } = await api.get("/market/prices", { timeout: 8000 });
        if (mounted && data?.coins?.length) {
          setCoins(data.coins);
          setIsLive(true);
          if (failStreak > 0) { failStreak = 0; scheduleNext(15000); }
        }
      } catch {
        if (!mounted) return;
        failStreak++;
        setIsLive(false);
        // Exponential backoff: 15s → 30s → 60s → 60s max (avoid hammering backend on mobile flaky net)
        const nextMs = Math.min(60000, 15000 * Math.pow(2, Math.min(failStreak, 3)));
        scheduleNext(nextMs);
      }
    };
    fetchPrices();
    iv = setInterval(fetchPrices, 15000);
    // Refresh on tab focus (users returning from other apps on mobile)
    const onFocus = () => { if (mounted && document.visibilityState === "visible") fetchPrices(); };
    document.addEventListener("visibilitychange", onFocus);
    return () => { mounted = false; clearInterval(iv); document.removeEventListener("visibilitychange", onFocus); };
  }, []);

  return { coins, isLive };
}

function Stat({ label, value, sub, testid, color, icon: Icon, to }) {
  const inner = (
    <>
      <div className="flex items-center justify-between text-xs uppercase tracking-widest text-slate-400">
        <span>{label}</span>
        {Icon && <Icon size={14} className="text-nb-cyan"/>}
      </div>
      <div className="mt-2 font-mono text-2xl md:text-3xl font-bold" style={{color: color || "#F8FAFC"}}>{value}</div>
      {sub && <div className="text-xs text-slate-500 mt-1 flex items-center justify-between"><span>{sub}</span>{to && <span className="text-[10px] text-nb-cyan uppercase tracking-widest">Tap →</span>}</div>}
    </>
  );
  if (to) {
    return (
      <Link to={to} data-testid={testid} className="nb-card p-5 block hover:border-nb-cyan/40 transition-colors">
        {inner}
      </Link>
    );
  }
  return (
    <div data-testid={testid} className="nb-card p-5">
      {inner}
    </div>
  );
}

// Feb-20: Small Dashboard tile showing the user's Credit Line Profit — sits in the row
// with Deposit/Withdrawal/Team so the mobile 2-col grid stays balanced.
function CreditProfitCard() {
  const [cl, setCl] = useState(null);
  useEffect(() => { (async () => { try { const { data } = await api.get("/credit/my"); setCl(data?.credit_line || null); } catch { /* ignore */ } })(); }, []);
  const released = Number(cl?.hold_profit_released || 0);
  const inHold = Number(cl?.hold_profit || 0);
  const totalEarned = Number(cl?.hold_profit_total_earned || (released + inHold) || 0);
  const status = cl?.status;
  const showReleased = status === "target_met" || status === "cleared";
  const badge = showReleased ? "Released" : (status === "expired" ? "Burnt" : "In Hold");
  const color = showReleased ? "#00E5FF" : (status === "expired" ? "#EF4444" : "#EAB308");
  return (
    <Link to="/credit" data-testid="stat-credit-profit" className="nb-card p-5 block hover:border-[#EAB308]/40 transition-colors">
      <div className="flex items-center justify-between text-xs uppercase tracking-widest text-slate-400">
        <span>Credit Line Profit</span>
        <Landmark size={14} className="text-nb-cyan"/>
      </div>
      <div className="mt-2 font-mono text-2xl md:text-3xl font-bold" style={{color}}>${totalEarned.toFixed(2)}</div>
      <div className="text-xs text-slate-500 mt-1 flex items-center justify-between">
        <span>{cl ? badge : "No credit line yet"}</span>
        <span className="text-[10px] text-nb-cyan uppercase tracking-widest">Tap →</span>
      </div>
    </Link>
  );
}

// Feb-20: Credit Wallet tile — always shows the user's Credit Facility amount + how
// much is currently locked into running credit-source bots, so users see credit stays
// visible even after being deployed into a Bot.
function CreditWalletCard() {
  const [cl, setCl] = useState(null);
  useEffect(() => { (async () => { try { const { data } = await api.get("/credit/my"); setCl(data?.credit_line || null); } catch { /* ignore */ } })(); }, []);
  const amount = Number(cl?.amount || 0);
  const locked = Number(cl?.locked_in_bots || 0);
  const available = Number(cl?.available_for_activation || 0);
  const status = cl?.status;
  const active = status === "approved" || status === "target_met";
  return (
    <Link to="/credit" data-testid="stat-credit-wallet" className="nb-card p-5 block hover:border-[#EAB308]/40 transition-colors">
      <div className="flex items-center justify-between text-xs uppercase tracking-widest text-slate-400">
        <span>Credit Wallet</span>
        <Wallet size={14} className="text-[#EAB308]"/>
      </div>
      <div className="mt-2 font-mono text-2xl md:text-3xl font-bold text-[#EAB308]">${amount.toFixed(2)}</div>
      <div className="text-xs text-slate-500 mt-1 flex items-center justify-between">
        <span>
          {!cl ? "No credit yet" : active ? <>Locked <span className="text-slate-300">${locked.toFixed(2)}</span> · Free <span className="text-[#8AE234]">${available.toFixed(2)}</span></> : (status === "cleared" ? "Cleared" : "Expired")}
        </span>
        <span className="text-[10px] text-nb-cyan uppercase tracking-widest">Tap →</span>
      </div>
    </Link>
  );
}

function LiveTicker({ ticker, isLive }) {
  return (
    <div className="nb-card p-0 overflow-hidden" data-testid="live-ticker">
      <div className="flex items-center justify-between px-4 pt-2 pb-1 border-b border-white/5">
        <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-slate-400">
          <div className="relative">
            <div className={`h-1.5 w-1.5 rounded-full ${isLive ? "bg-[#8AE234]" : "bg-slate-500"}`}/>
            {isLive && <div className="absolute inset-0 h-1.5 w-1.5 rounded-full bg-[#8AE234] animate-ping"/>}
          </div>
          {isLive ? "Live · Real-time" : "Cached · Reconnecting"}
        </div>
        <div className="text-[10px] uppercase tracking-widest text-slate-500">Powered by OKX</div>
      </div>
      <div className="flex overflow-x-auto no-scrollbar divide-x divide-white/5">
        {ticker.map((x) => {
          const up = (x.change_24h || 0) >= 0;
          return (
            <div key={x.pair || x.id} data-testid={`ticker-${x.symbol}`} className="min-w-[160px] px-4 py-3">
              <div className="flex items-center gap-1.5 text-xs text-slate-400 font-mono">
                {x.image && <img src={x.image} alt={x.symbol} className="h-3.5 w-3.5 rounded-full"/>}
                {x.pair}
              </div>
              <div className="font-mono text-white text-sm mt-0.5">
                {x.price < 1 ? x.price.toFixed(4) : x.price.toLocaleString(undefined, { maximumFractionDigits: 2 })}
              </div>
              <div className={`text-xs font-mono flex items-center gap-1 ${up ? "text-[#8AE234]" : "text-red-400"}`}>
                {up ? <ArrowUp size={10}/> : <ArrowDown size={10}/>} {Math.abs(x.change_24h || 0).toFixed(2)}%
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function BotSignals({ hasBot }) {
  const [signals, setSignals] = useState([]);
  useEffect(() => {
    if (!hasBot) return;
    let mounted = true;
    const fetchSig = async () => {
      try { const { data } = await api.get("/trades/signals"); if (mounted) setSignals(data.signals || []); }
      catch { /* noop */ }
    };
    fetchSig();
    const iv = setInterval(fetchSig, 4500);
    return () => { mounted = false; clearInterval(iv); };
  }, [hasBot]);

  return (
    <div className="nb-card p-5 overflow-hidden">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2 min-w-0">
          <div className="relative">
            <div className="h-2 w-2 rounded-full bg-[#8AE234]"/>
            <div className="absolute inset-0 h-2 w-2 rounded-full bg-[#8AE234] animate-ping"/>
          </div>
          <div className="font-heading font-semibold text-white text-sm flex items-center gap-2"><Radar size={14} className="text-[#8AE234]"/> Bot Signal Scanner</div>
        </div>
        <div className="text-[10px] text-slate-500 uppercase tracking-widest">Live</div>
      </div>
      {!hasBot ? (
        <div className="text-sm text-slate-500 py-6 text-center">
          Activate a bot to start receiving trade signals.
          <div className="mt-3"><Link to="/bots" className="nb-btn-primary text-xs">Choose bot</Link></div>
        </div>
      ) : signals.length === 0 ? (
        <div className="text-sm text-slate-500 py-4 text-center">Scanning market…</div>
      ) : (
        <div className="space-y-2">
          {signals.map((s) => (
            <div key={s.id} data-testid={`signal-${s.id}`} className="flex items-center justify-between rounded-lg border border-white/5 bg-[#070B14] px-3 py-2">
              <div className="flex items-center gap-3">
                <span className={`text-[10px] font-bold px-2 py-0.5 rounded uppercase ${s.side==="LONG"?"bg-[#8AE234]/10 text-[#8AE234]":"bg-red-500/10 text-red-400"}`}>{s.side}</span>
                <div>
                  <div className="text-sm text-white font-mono">{s.pair}</div>
                  <div className="text-[10px] text-slate-500 capitalize">{s.status} · ETA {s.eta_sec}s</div>
                </div>
              </div>
              <div className="text-right">
                <div className="font-mono text-xs text-slate-300">${s.price < 10 ? s.price.toFixed(4) : s.price.toFixed(2)}</div>
                <div className="text-[10px] font-mono text-nb-cyan">{s.confidence}% conf</div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function TradesFeed() {
  const [trades, setTrades] = useState([]);
  useEffect(() => { (async () => { try { const { data } = await api.get("/trades/my?limit=15"); setTrades(data.trades); } catch {} })(); }, []);
  return (
    <div className="nb-card p-5">
      <div className="flex items-center justify-between mb-3">
        <div className="font-heading font-semibold text-white flex items-center gap-2"><Activity size={14} className="text-nb-cyan"/> Recent Trades</div>
        <Link to="/trades" className="text-xs text-nb-cyan hover:text-white">All trades <ArrowRight size={10} className="inline"/></Link>
      </div>
      {trades.length === 0 ? (
        <div className="text-sm text-slate-500 py-6 text-center">Trade history appears here after your bot runs.</div>
      ) : (
        <div className="divide-y divide-white/5 max-h-72 overflow-y-auto">
          {trades.map((t) => (
            <div key={t.id} className="flex items-center justify-between py-2.5 text-xs">
              <div className="flex items-center gap-3">
                <span className={`text-[10px] font-bold px-2 py-0.5 rounded uppercase ${t.side==="LONG"?"bg-[#8AE234]/10 text-[#8AE234]":"bg-red-500/10 text-red-400"}`}>{t.side}</span>
                <div>
                  <div className="text-white font-mono">{t.pair}</div>
                  <div className="text-[10px] text-slate-500">Entry ${t.entry < 10 ? t.entry.toFixed(4) : t.entry.toFixed(2)} → Exit ${t.exit < 10 ? t.exit.toFixed(4) : t.exit.toFixed(2)}</div>
                </div>
              </div>
              <div className="text-right">
                <div className={`font-mono ${t.pnl >= 0 ? "text-[#8AE234]" : "text-red-400"}`}>{t.pnl >= 0 ? "+" : "-"}${Math.abs(t.pnl).toFixed(4)}</div>
                <div className="text-[10px] text-slate-500">{t.duration_min}m</div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function IncomeBreakdown() {
  const [data, setData] = useState(null);
  useEffect(() => { (async () => { const { data: d } = await api.get("/income/breakdown"); setData(d); })(); }, []);
  if (!data) return null;
  const b = data.breakdown;
  const items = [
    { key: "daily_profit", label: "Bot Profit", color: "#8AE234" },
    { key: "referral_bonus", label: "Referral Bonus", color: "#00E5FF" },
    { key: "team_income", label: "Team Income", color: "#A855F7" },
    { key: "leadership_bonus", label: "Leadership Bonus", color: "#EAB308" },
  ];
  return (
    <div className="nb-card p-5">
      <div className="font-heading font-semibold text-white mb-4">Income Breakdown</div>
      <div className="space-y-2">
        {items.map((it) => {
          const row = b[it.key] || {total: 0, count: 0};
          return (
            <div key={it.key} data-testid={`income-${it.key}`} className="flex items-center justify-between rounded-lg border border-white/5 bg-[#070B14] px-3 py-2">
              <div className="flex items-center gap-2">
                <div className="h-2 w-2 rounded-full" style={{background: it.color}}/>
                <div className="text-sm text-slate-200">{it.label}</div>
              </div>
              <div className="text-right">
                <div className="font-mono text-white text-sm">${row.total.toFixed(2)}</div>
                <div className="text-[10px] text-slate-500">{row.count} entries</div>
              </div>
            </div>
          );
        })}
      </div>
      <div className="mt-4 border-t border-white/5 pt-3 grid grid-cols-2 gap-3 text-xs">
        <div>
          <div className="text-slate-500 uppercase tracking-widest">Total Deposit</div>
          <div className="font-mono text-white">${data.totals.deposit.toFixed(2)}</div>
        </div>
        <div>
          <div className="text-slate-500 uppercase tracking-widest">Total Withdrawal</div>
          <div className="font-mono text-white">${data.totals.withdrawal.toFixed(2)}</div>
        </div>
      </div>
    </div>
  );
}

export default function Dashboard() {
  const { user } = useAuth();
  const [stats, setStats] = useState({ levels: [], direct_count: 0, active_directs: 0 });
  const [pubCfg, setPubCfg] = useState(null);
  const { coins, isLive } = useLivePrices();
  const hasBot = !!user?.active_bot_id;

  useEffect(() => {
    (async () => {
      try {
        const [s, c] = await Promise.all([
          api.get("/team/stats"),
          api.get("/public/config").catch(() => ({ data: null })),
        ]);
        setStats(s.data); setPubCfg(c.data);
      } catch {}
    })();
  }, []);

  const refLink = `${window.location.origin}/register?ref=${user?.referral_code || ""}`;
  const copyRef = async () => { await navigator.clipboard.writeText(refLink); toast.success("Referral link copied"); };
  const rankColor = RANK_COLORS[user?.rank || "core"];

  return (
    <AppLayout variant="user">
      <AnnouncementPopup />
      <NBFirstDepositModal />
      <RiskBanner />
      <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
        <div>
          <div className="text-xs uppercase tracking-widest text-nb-cyan">Welcome back</div>
          <h1 className="font-heading font-bold text-3xl md:text-4xl text-white mt-1">{user?.name}</h1>
        </div>
        <div data-testid="dashboard-rank-badge" className="px-4 py-2 rounded-full border font-heading uppercase text-xs tracking-widest" style={{color: rankColor, borderColor: rankColor}}>
          <Crown size={12} className="inline mr-1 -mt-0.5"/> {RANK_LABEL[user?.rank || "core"]} Tier
        </div>
      </div>

      <LiveTicker ticker={coins} isLive={isLive} />

      <div className="mt-6 grid grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
        <NBTokenCard/>
        <Stat testid="stat-balance" label="Wallet Balance" value={`$${(user?.balance||0).toFixed(2)}`} sub="USDT" color="#8AE234" icon={Wallet}/>
        <Stat testid="stat-total-profit" label="Total Profit" value={`$${((user?.total_profit||0)+(user?.total_team_income||0)+(user?.total_referral_bonus||0)+(user?.total_leadership_bonus||0)+(user?.total_shareholder_bonus||0)+(user?.total_shareholder_commission||0)).toFixed(2)}`} sub="Bot + Team + Referral + Leadership · view history" color="#00E5FF" icon={TrendingUp} to="/transactions?filter=profit"/>
        <Stat testid="stat-total-income" label="Team + Referral" value={`$${((user?.total_team_income||0)+(user?.total_referral_bonus||0)+(user?.total_leadership_bonus||0)).toFixed(2)}`} sub="View network earnings" color="#A855F7" icon={Users2} to="/transactions?filter=network"/>
      </div>

      {/* Feb-2026: 6-card secondary row (no gaps on mobile 2-col or desktop 3-col) */}
      <div className="mt-4 grid grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
        <Stat testid="stat-active-inv" label="Active Investment" value={`$${(user?.active_investment||0).toFixed(2)}`} sub={hasBot?`Bot-${user.active_bot_id} · locked`:"No bot"} icon={Bot}/>
        <Stat testid="stat-total-deposit" label="Total Deposit" value={`$${(user?.total_deposit||0).toFixed(2)}`} sub="View deposit history" color="#8AE234" icon={ArrowDownToLine} to="/deposit"/>
        <Stat testid="stat-total-withdrawal" label="Total Withdrawal" value={`$${(user?.total_withdrawal||0).toFixed(2)}`} sub="View withdrawal history" color="#EF4444" icon={ArrowUpFromLine} to="/withdraw"/>
        <Stat testid="stat-total-team" label="Total Team Members" value={String(user?.total_team_size ?? 0)} sub="All levels · view network" color="#A855F7" icon={Users2} to="/team"/>
        <CreditWalletCard/>
        <CreditProfitCard/>
      </div>

      {/* Feb-2026: Today Profit + Leadership Status */}
      <div className="mt-6 grid md:grid-cols-2 gap-4 md:gap-6">
        <TodayProfitCard/>
        <LeadershipStatusCard/>
      </div>

      {/* Feb-2026: Tour eligibility */}
      <div className="mt-6">
        <TourEligibilityCard/>
      </div>

      {/* Feb-21: Tour Rewards Fund — auto-eligibility + admin-toggled withdrawal */}
      <div className="mt-6">
        <TourFundCard/>
      </div>

      {/* Feb-19: Company Credit Line — status + repayment + hold profit */}
      <div className="mt-6">
        <CreditLineCard/>
      </div>

      {/* Staking + Shareholder home page cards (item 14) */}
      <div className="mt-6 grid md:grid-cols-2 gap-4 md:gap-6" data-testid="dashboard-products">
        <Link to="/staking" data-testid="dashboard-staking-card" className="group nb-card p-5 relative overflow-hidden hover:border-nb-cyan/40 transition-colors">
          <div className="absolute -right-8 -top-8 h-32 w-32 rounded-full bg-[#00E5FF]/10 blur-2xl"/>
          <div className="flex items-start justify-between gap-3">
            <div>
              <div className="text-[10px] uppercase tracking-widest text-nb-cyan flex items-center gap-1">Long-Term Staking · 365 days</div>
              <div className="font-heading text-white text-xl mt-1">Multiply your USDT up to <span className="text-nb-cyan">4×</span></div>
              <p className="text-slate-400 text-xs mt-2 max-w-xs">Lock USDT for 365 days. Full principal + reward unlocks on day 365.</p>
              <div className="mt-3 grid grid-cols-3 gap-2 text-[10px]">
                <div><div className="text-slate-500 uppercase tracking-widest">Staked</div><div className="text-white font-mono">${(user?.staked_principal||0).toFixed(2)}</div></div>
                <div><div className="text-slate-500 uppercase tracking-widest">Reward pending</div><div className="text-[#EAB308] font-mono">${(user?.pending_staking_rewards||0).toFixed(2)}</div></div>
                <div><div className="text-slate-500 uppercase tracking-widest">Active</div><div className="text-nb-cyan font-mono">{user?.active_stakes_count||0}</div></div>
              </div>
            </div>
            <div className="rounded-2xl border border-nb-cyan/30 bg-nb-cyan/10 p-3 shrink-0"><Lock size={22} className="text-nb-cyan"/></div>
          </div>
          <div className="mt-4 text-xs text-nb-cyan font-mono group-hover:translate-x-1 transition-transform">Open Staking →</div>
        </Link>

        <Link to="/shareholder" data-testid="dashboard-shareholder-card" className="group nb-card p-5 relative overflow-hidden hover:border-[#EAB308]/40 transition-colors">
          <div className="absolute -right-8 -top-8 h-32 w-32 rounded-full bg-[#EAB308]/10 blur-2xl"/>
          <div className="flex items-start justify-between gap-3">
            <div>
              <div className="text-[10px] uppercase tracking-widest text-[#EAB308] flex items-center gap-1">
                Company Shareholder
                {user?.shareholder_level && (
                  <span data-testid="dashboard-shareholder-badge" className="ml-1 px-2 py-0.5 rounded-full bg-[#EAB308]/20 text-[#EAB308] font-mono">
                    {user.shareholder_level.toUpperCase()} · {user.shareholder_pct}%
                  </span>
                )}
              </div>
              <div className="font-heading text-white text-xl mt-1">
                {user?.shareholder_level ? (
                  <>Earning <span className="text-[#EAB308]">{user.shareholder_pct}%</span> on every new business volume</>
                ) : (
                  <>Earn up to <span className="text-[#EAB308]">15%</span> real-time on your network's activity</>
                )}
              </div>
              <p className="text-slate-400 text-xs mt-2 max-w-xs">
                {user?.shareholder_level
                  ? "Commission credits to your balance instantly whenever anyone in your network adds new business volume."
                  : "Reach $2,000 Total Balance to unlock Silver Shareholder — commission credits live, no monthly waiting."}
              </p>
              <div className="mt-3 grid grid-cols-3 gap-2 text-[10px]">
                <div><div className="text-slate-500 uppercase tracking-widest">Total Balance</div><div className="text-white font-mono">${(user?.total_holdings||0).toFixed(2)}</div></div>
                <div><div className="text-slate-500 uppercase tracking-widest">Commission</div><div className="text-[#8AE234] font-mono">${(user?.total_shareholder_commission||0).toFixed(2)}</div></div>
                <div><div className="text-slate-500 uppercase tracking-widest">Level</div><div className="text-[#EAB308] font-mono">{user?.shareholder_level ? user.shareholder_level.toUpperCase() : "—"}</div></div>
              </div>
            </div>
            <div className="rounded-2xl border border-[#EAB308]/30 bg-[#EAB308]/10 p-3 shrink-0"><Building2 size={22} className="text-[#EAB308]"/></div>
          </div>
          <div className="mt-4 text-xs text-[#EAB308] font-mono group-hover:translate-x-1 transition-transform">Open Shareholder →</div>
        </Link>
      </div>

      {/* Plan / PDF download strip */}
      <Link to="/plan" data-testid="dashboard-plan-card" className="group mt-6 block nb-card p-5 relative overflow-hidden hover:border-nb-cyan/40 transition-colors">
        <div className="absolute -right-6 -top-6 h-40 w-40 rounded-full bg-nb-cyan/10 blur-3xl"/>
        <div className="flex flex-wrap items-center gap-4 justify-between">
          <div className="flex items-center gap-4">
            <div className="rounded-2xl border border-nb-cyan/30 bg-nb-cyan/10 p-3 shrink-0">
              <FileText size={22} className="text-nb-cyan"/>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-widest text-nb-cyan">The Plan · Premium PDF</div>
              <div className="font-heading text-white text-xl mt-1">Everything about Next Billion</div>
              <div className="text-xs text-slate-400 mt-1">Bots, team commissions, shareholder cascade, ranks — one premium PDF you can share with prospects.</div>
            </div>
          </div>
          <div className="flex gap-2 shrink-0">
            <a
              href={`${process.env.REACT_APP_BACKEND_URL}/api/marketing/plan.pdf`}
              download="next-billion-plan.pdf"
              target="_blank"
              rel="noreferrer"
              onClick={(e)=>e.stopPropagation()}
              data-testid="dashboard-plan-download"
              className="nb-btn-primary text-sm inline-flex items-center gap-2"
            >
              <Download size={14}/> Download PDF
            </a>
            <span className="nb-btn-secondary text-sm inline-flex items-center gap-2">
              View <ArrowRight size={14}/>
            </span>
          </div>
        </div>
      </Link>

      <div className="mt-6 grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 min-w-0">
          <MarketChart coins={coins}/>
        </div>

        <BotSignals hasBot={hasBot} />
      </div>

      <div className="mt-6 grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 min-w-0">
          <TradesFeed />
        </div>
        <IncomeBreakdown />
      </div>

      <div className="mt-6 grid lg:grid-cols-3 gap-6">
        <div className="nb-card p-5 lg:col-span-2 min-w-0">
          <div className="flex items-center justify-between mb-4">
            <div className="font-heading font-semibold text-white">Team overview</div>
            <Link to="/team" data-testid="link-team-tree" className="text-xs text-nb-cyan hover:text-white">View tree <ArrowRight size={12} className="inline"/></Link>
          </div>
          <div className="grid grid-cols-5 gap-3">
            {[1,2,3,4,5].map((lv) => {
              const l = stats.levels.find(x=>x.level===lv) || { level: lv, total_members: 0, active_members: 0, volume: 0 };
              return (
                <div key={lv} data-testid={`level-${lv}`} className="rounded-xl border border-white/10 bg-[#070B14] p-3">
                  <div className="text-[10px] uppercase text-slate-500">Lv {lv}</div>
                  <div className="font-mono text-xl text-white">{l.active_members}</div>
                  <div className="text-[10px] text-slate-500">/ {l.total_members}</div>
                  <div className="text-[10px] font-mono text-nb-cyan mt-1">${l.volume.toFixed(0)}</div>
                </div>
              );
            })}
          </div>
          <div className="mt-4 flex items-center gap-2 text-xs text-slate-400">
            <Users2 size={14}/> {stats.direct_count} direct · {stats.active_directs} active
          </div>
        </div>

        <div className="nb-card p-5">
          <div className="flex items-center justify-between mb-3">
            <div className="font-heading font-semibold text-white">Your referral link</div>
            <button data-testid="btn-copy-referral" onClick={copyRef} className="text-nb-cyan hover:text-white text-xs flex items-center gap-1"><Copy size={12}/> Copy</button>
          </div>
          <div className="font-mono text-xs bg-[#070B14] border border-white/10 rounded-lg px-3 py-2 text-slate-300 break-all">{refLink}</div>
          <div className="mt-3 text-xs text-slate-500">Code: <span className="font-mono text-nb-cyan">{user?.referral_code}</span></div>
          <div className="mt-4 text-xs text-slate-400">Earn <span className="neon-text font-mono">{pubCfg?.direct_referral_bonus_pct ?? 5}%</span> on every direct referral's first investment.</div>
        </div>
      </div>
      <TelegramSupportButton/>
    </AppLayout>
  );
}
